import { provide } from 'vue'
import { createApolloProvider } from '@vue/apollo-option'
import {
  ApolloClient,
  defaultDataIdFromObject,
  InMemoryCache,
  from,
  ApolloLink,
} from '@apollo/client/core'
import { ApolloClients } from '@vue/apollo-composable'
import createUploadLink from 'apollo-upload-client/createUploadLink.mjs'
import { sha256 } from 'crypto-hash'
import { createPersistedQueryLink } from '@apollo/client/link/persisted-queries'
import { loadDevMessages } from '@apollo/client/dev'
import { ErrorHandlingLink } from '@/app-bootstrapping/apollo/ErrorHandlingLink'
import { apolloListener } from '@/utils/Sentry' // might need to update this little
import possibleTypes from '@/possibleTypes.json'
import { merge, textHelpers } from '@/utils/tools'
import { getAvatarPath } from '@/utils/avatar'
import { WorkflowVariableOrigins } from '@/gql/graphql'
import { HandleLongGETRequestURIs } from '@/app-bootstrapping/apollo/HandleLongGETRequestURIs.ts'

if (import.meta.env.DEV) {
  loadDevMessages()
}

export const apolloClient = new ApolloClient({
  devtools: { enabled: import.meta.env.DEV },
  link: from([
    ErrorHandlingLink,
    createUploadLink({
      uri: `${window.location.origin}/web-api/graphql`,
      headers: {
        'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]').getAttribute('content'),
      },
    }),
  ]),
  cache: new InMemoryCache({
    possibleTypes,
    dataIdFromObject: (object) => {
      // eslint-disable-next-line no-nested-ternary
      const key = object.globalId
        ? `${atob(object.globalId)}`
        : object.localId
          ? `${object.__typename}:${object.localId}`
          : null
      return key
    },
  }),
})

const uri = document.querySelector('meta[name="public-api-uri"]').getAttribute('content')

const namedLink = new ApolloLink((operation, forward) => {
  operation.setContext(() => ({
    uri: `${uri}?${operation.operationName}`,
  }))
  return forward ? forward(operation) : null
})

const PersistedQueryLink = createPersistedQueryLink({
  sha256,
  useGETForHashedQueries: true,
})

const uploadLink = createUploadLink({
  uri,
  headers: {
    Authorization: `Bearer ${document
      .querySelector('meta[name="api-token"]')
      ?.getAttribute('content')}`,
  },
})

const publicApiChain = from([
  namedLink,
  ErrorHandlingLink,
  PersistedQueryLink,
  HandleLongGETRequestURIs,
  uploadLink,
])

// Native public api, does not interfere with the native public api gql
export const publicApi = new ApolloClient({
  clientAwareness: { name: 'Platform (Web)', version: import.meta.env.VITE_APP_VERSION },
  devtools: { enabled: import.meta.env.DEV },
  link: publicApiChain,
  cache: new InMemoryCache({
    possibleTypes: {
      HasMultiFactorMetadata: ['SmsMultiFactor', 'TotpMultiFactor'],
      MultiFactor: ['SmsMultiFactor', 'TotpMultiFactor'],
      ClientRelation: ['CompanySupplier', 'SupplierClient'],
      Ownable: ['CompanySupplier', 'Hire', 'SupplierClient'],
    },
    typePolicies: {
      Company: {
        fields: {
          // Address has no id, so partial selections would replace each other instead of merging.
          address: {
            merge: true,
          },
        },
      },
      Query: {
        fields: {
          // Lets ClientChip resolve clientRelation(id) from rows and pages that already cached it.
          clientRelation: {
            read(existingData, { args, toReference }) {
              if (existingData) return existingData
              let typename
              try {
                typename = atob(args?.id ?? '').split(':')[0]
              } catch {
                return undefined
              }
              if (!['CompanySupplier', 'SupplierClient'].includes(typename)) return undefined
              return toReference({ __typename: typename, id: args.id })
            },
          },
          // Helps map the job query (singular) to the lists query (jobs) cached elements
          job: {
            read(existingData, { args, toReference }) {
              return (
                existingData ??
                toReference({
                  __typename: 'Job',
                  id: args?.id,
                })
              )
            },
          },
        },
      },
    },
  }),
})

// Similar to the public api, but it allows for more customization.
// You need a custom resolver, field or type add them here
// https://www.apollographql.com/docs/react/caching/cache-field-behavior
export const publicApiCustomFields = new ApolloClient({
  clientAwareness: { name: 'Platform (Web)', version: import.meta.env.VITE_APP_VERSION },
  devtools: { enabled: import.meta.env.DEV },
  link: publicApiChain,
  cache: new InMemoryCache({
    possibleTypes: {
      HasMultiFactorMetadata: ['SmsMultiFactor', 'TotpMultiFactor'],
      MultiFactor: ['SmsMultiFactor', 'TotpMultiFactor'],
      WorkflowVariable: ['WorkflowVariableStringType', 'WorkflowVariableOptionType'],
    },
    dataIdFromObject(responseObject) {
      switch (responseObject.__typename) {
        case 'CustomField':
          // Due to less than ideal schema design CustomFields returned with rules overlap and cause consistency issues
          // This edge case is covered here, but an idea solution would be an improved schema
          if (responseObject?.rule?.__ref) {
            return `CustomField:${responseObject.id}:${responseObject?.rule?.__ref}`
          }
          return defaultDataIdFromObject(responseObject)
        default:
          return defaultDataIdFromObject(responseObject)
      }
    },
    typePolicies: {
      Query: {
        fields: {
          approval(_, { args, toReference }) {
            return toReference({
              __typename: 'Approval',
              id: args.id,
            })
          },
          projects: {
            keyArgs: ['accounts', 'search', 'status', 'owners'],
            merge,
          },
          jobs: {
            keyArgs: [
              'accounts',
              'search',
              'markets',
              'currencies',
              'rateTypes',
              'paymentSchemes',
              'associations',
              'locationPreferences',
              'completed',
              'hasProject',
              'hasHire',
              'owners',
              'markets',
              'paymentSchemes',
              'requiredSkills',
              'experienceLevel',
              'available',
              'removed',
              'approvalState',
              'hasBids',
              'externalIdentifiers',
              'orderBy',
              'createdAtDateRange',
              'unfilled',
              'customFields',
              'startDateRange',
              'endDateRange',
            ],
            merge,
          },
          customFields: {
            keyArgs: ['accounts', 'appliesTo'],
            merge,
          },
          userGroups: {
            keyArgs: ['search', 'first', 'accounts'],
            merge,
          },
          skills: {
            keyArgs: ['search', 'skillableType', 'orderBy'],
            merge,
          },
        },
      },
      Organisation: {
        fields: {
          avatarPath: {
            read(_, { readField }) {
              return getAvatarPath(readField('avatar'), readField('id'))
            },
          },
        },
      },
      Company: {
        fields: {
          teamMembers: {
            keyArgs: false,
            merge,
          },
          teamMembersAndOwners: {
            keyArgs: ['search'],
            merge,
          },
          avatarPath: {
            read(_, { readField }) {
              return getAvatarPath(readField('avatar'), readField('id'))
            },
          },
        },
      },
      Worker: {
        fields: {
          avatarPath: {
            read(_, { readField }) {
              return getAvatarPath(readField('avatar'), readField('id'))
            },
          },
        },
      },
      User: {
        fields: {
          initials: {
            read(_, { readField }) {
              return textHelpers.nameStringToInitials(readField('name'), 2)
            },
          },
          avatarPath: {
            read(_, { readField }) {
              return getAvatarPath(readField('avatar'), readField('id'))
            },
          },
        },
      },
      WorkflowVariableStringType: {
        fields: {
          originMeta: {
            read(_, { readField }) {
              if (isNaN(Number(readField('id')))) {
                return WorkflowVariableOrigins.SystemVariable
              }
              return WorkflowVariableOrigins.CustomVariable
            },
          },
        },
      },

      WorkflowVariableOptionType: {
        fields: {
          originMeta: {
            read(_, { readField }) {
              if (isNaN(Number(readField('id')))) {
                return WorkflowVariableOrigins.SystemVariable
              }
              return WorkflowVariableOrigins.CustomVariable
            },
          },
        },
      },
    },
  }),
})

export const clients = {
  apolloClient: apolloListener(apolloClient),
  default: apolloListener(apolloClient),
  publicApi: apolloListener(publicApi),
  publicApiCustomFields: apolloListener(publicApiCustomFields),
}

export const provider = createApolloProvider({
  defaultClient: apolloListener(apolloClient),
  clients,
})

window.apolloProvider = provider

export function setUpApollo(app) {
  app.use(provider)
}

export function provideApollo() {
  provide(ApolloClients, clients)
}
